import { getExperienceImageUrl } from "@/environments/environment";

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../../../hooks/useCart';
// @ts-ignore - react-phone-input-2 has no bundled types in this project
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import groupPackages1 from '../../group-tours/data/groupPackages1';
import { travelPackages } from '../../experience/components/PackagesSection';
import experiencesService from '@/services/experiences';

export const CartSection: React.FC = () => {
  const navigate = useNavigate();
  const { cartItems, addToCart, removeFromCart, updateQuantity, getCartTotal, clearCart } = useCart();
  const [contactName, setContactName] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [travelDate, setTravelDate] = useState('');
  const [adults, setAdults] = useState<number>(2);
  const [children, setChildren] = useState<number>(0);
  const [message, setMessage] = useState('');

  const handleSubmitBooking = () => {
    // basic validation
    if (!contactName.trim() || !contactPhone.trim() || !contactEmail.trim()) {
      alert('Please provide your name, phone and email before submitting.');
      return;
    }

    // Simulate successful submission
    alert('Booking request submitted successfully!');

    // Optionally clear form
    setContactName('');
    setContactPhone('');
    setContactEmail('');
    setTravelDate('');
    setAdults(2);
    setChildren(0);
    setMessage('');
  };

  const handleContinueShopping = () => {
    navigate('/experience');
  };

  // Totals used in booking summary - compute early so nested components can reference if needed
  const subtotal = getCartTotal();
  const tax = subtotal * 0.18; // 18% GST
  const total = subtotal + tax;

  // Simple itinerary renderer used inside cart rows
  const ItineraryAccordion: React.FC<{ itinerary: Array<any> }> = ({ itinerary }) => {
    if (!itinerary || itinerary.length === 0) return null;
    return (
      <div className="space-y-3">
        {itinerary.map((it: any, idx: number) => (
          <div key={idx} className="p-2 bg-gray-50 rounded">
            <div className="text-sm font-semibold text-gray-800">{it.title || `Day ${idx + 1}`}</div>
            <div className="text-sm text-gray-600">{it.content || it.description || it.details || ''}</div>
          </div>
        ))}
      </div>
    );
  };

  const CartRow: React.FC<{
    item: typeof cartItems[number];
    updateQuantity: (id: string, qty: number) => void;
    removeFromCart: (id: string) => void;
  }> = ({ item, updateQuantity, removeFromCart }) => {
    const [level, setLevel] = useState<0 | 1 | 2>(0);
    const descRef = useRef<HTMLDivElement | null>(null);
    const itinRef = useRef<HTMLDivElement | null>(null);
    const [maxHeight, setMaxHeight] = useState<number>(0);

    // measure heights when level changes
    useEffect(() => {
      const shortH = descRef.current ? descRef.current.scrollHeight : 0;
      const itinH = itinRef.current ? itinRef.current.scrollHeight : 0;
      if (level === 0) setMaxHeight(0);
      else if (level === 1) setMaxHeight(shortH + 8); // small padding
      else setMaxHeight(shortH + itinH + 12);
    }, [level]);

    return (
      <div className="border border-gray-200 rounded-lg bg-white overflow-hidden">
        <div className="p-4">
          <div className="flex items-start gap-4">
            <img src={item.image} alt={item.title} className="w-16 h-16 object-cover rounded-lg flex-shrink-0" />
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium text-gray-900 text-sm line-clamp-2">{item.title}</h4>
                  <p className="text-xs text-gray-500">{item.category}</p>
                </div>
                <div className="text-sm font-semibold text-emerald-600">₹{(item.price * item.quantity).toLocaleString()}</div>
              </div>

              <div className="mt-2 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => updateQuantity(item.id, Math.max(1, item.quantity - 1))}
                    className="w-6 h-6 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded text-sm"
                  >
                    -
                  </button>
                  <span className="text-sm font-medium">{item.quantity}</span>
                  <button
                    onClick={() => updateQuantity(item.id, item.quantity + 1)}
                    className="w-6 h-6 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded text-sm"
                  >
                    +
                  </button>
                </div>

                <div className="flex items-center gap-2">
                  <button onClick={() => removeFromCart(item.id)} className="text-red-500 hover:text-red-700 p-2">
                    <i className="ri-delete-bin-line text-xl"></i>
                  </button>
                  {level === 0 && (
                    <button onClick={() => setLevel(1)} className="bg-emerald-600 text-white px-3 py-1 rounded-md text-sm">View</button>
                  )}
                  {level === 1 && (
                    <>
                      <button onClick={() => setLevel(2)} className="bg-emerald-600 text-white px-3 py-1 rounded-md text-sm">View More</button>
                      <button onClick={() => setLevel(0)} className="ml-2 text-sm text-gray-600">Hide</button>
                    </>
                  )}
                  {level === 2 && (
                    <>
                      <button onClick={() => setLevel(0)} className="bg-gray-100 text-gray-800 px-3 py-1 rounded-md text-sm">Collapse</button>
                    </>
                  )}
                </div>
              </div>

              <div
                style={{ maxHeight: `${maxHeight}px`, transition: 'max-height 350ms ease, opacity 250ms ease' }}
                className="overflow-hidden mt-3"
                aria-hidden={level === 0}
              >
                <div ref={descRef} className="text-sm text-gray-700 mb-2 opacity-100">
                  <p className="">{item.description || 'No description available.'}</p>
                </div>

                <div ref={itinRef} className="mt-2">
                  {item.itinerary && <ItineraryAccordion itinerary={item.itinerary} />}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };
  // If cart is empty but an id param is present, try to find the package and add it
  useEffect(() => {
    if (cartItems.length > 0) return;
    const params = new URLSearchParams(window.location.search);
    const id = params.get('id');
    if (!id) return;

    // If id is prefixed for experience packages (exp-<id>), handle that
    if (id.startsWith('exp-')) {
      const raw = id.replace(/^exp-/, '');
      const tp = travelPackages.find(p => p.id === raw);
      if (tp) {
        // add with prefixed id so it matches items added from Experience page
        addToCart({ id: `exp-${tp.id}`, title: tp.title, price: tp.price, image: tp.image, category: tp.category, description: tp.description, itinerary: tp.itinerary });
        return;
      }

      // If static travelPackages are empty (data comes from API), fetch the experience and add it
      (async () => {
        try {
          const exp = await experiencesService.getExperience(raw);
          if (exp) {
            const image = getExperienceImageUrl(exp.imageUrl) || '/images/experience-package-2.jpg';

            addToCart({ id: `exp-${exp.id || exp._id}`, title: exp.title, price: exp.price || 0, image, category: exp.category || 'General', description: exp.description || '', itinerary: exp.itinerary || [] });
          }
        } catch (err) {
          console.warn('Failed to fetch experience for cart fallback', err);
        }
      })();
      return;
    }

    // Try group packages first (group ids are not prefixed)
    const gp = groupPackages1.find(p => p.id === id);
    if (gp) {
      addToCart({ id: gp.id, title: gp.tourTitle, price: gp.price.total, image: gp.imageUrl, category: gp.category, description: gp.description, itinerary: gp.itinerary });
      return;
    }

    // Try travel packages by raw id (if URL used raw id)
    const tp = travelPackages.find(p => p.id === id);
    if (tp) {
      addToCart({ id: `exp-${tp.id}`, title: tp.title, price: tp.price, image: tp.image, category: tp.category, description: tp.description, itinerary: tp.itinerary });
      return;
    }
  }, [cartItems.length, addToCart]);

  if (cartItems.length === 0) {
    return (
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <i className="ri-shopping-cart-line text-8xl text-gray-300 mb-6"></i>
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Your Cart is Empty</h1>
          <p className="text-gray-600 mb-8">
            Looks like you haven't added any travel packages to your cart yet.
          </p>
          <button
            onClick={handleContinueShopping}
            className="bg-emerald-600 text-white px-8 py-3 rounded-lg hover:bg-emerald-700 transition-colors duration-200 font-medium whitespace-nowrap"
          >
            Browse Packages
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16">
      <div className="max-w-6xl mx-auto px-6">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Shopping Cart</h1>
          <p className="text-gray-600">{cartItems.length} items in your cart</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Cart Items - larger package-style cards */}
          <div className="lg:col-span-2 space-y-8">
            {/* Compact cart rows with progressive reveal */}
            <div className="space-y-4">
              {cartItems.map(item => (
                <CartRow
                  key={item.id}
                  item={item}
                  updateQuantity={updateQuantity}
                  removeFromCart={removeFromCart}
                />
              ))}
            </div>

            <div className="flex justify-between">
              <button
                onClick={handleContinueShopping}
                className="text-emerald-600 hover:text-emerald-700 font-medium flex items-center gap-2"
              >
                <i className="ri-arrow-left-line"></i>
                Continue Shopping
              </button>
            </div>
          </div>

          {/* Booking Summary and Contact */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-lg p-6 sticky top-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Booking Summary</h2>

              <div className="space-y-3 mb-4">
                <div className="flex justify-between">
                  <span className="text-gray-600">Subtotal</span>
                  <span className="font-medium">₹{subtotal.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">GST (18%)</span>
                  <span className="font-medium">₹{tax.toLocaleString()}</span>
                </div>
                <div className="border-t border-gray-200 pt-4">
                  <div className="flex justify-between">
                    <span className="text-lg font-semibold text-gray-900">Total</span>
                    <span className="text-xl font-bold text-emerald-600">₹{total.toLocaleString()}</span>
                  </div>
                </div>
              </div>

              <div className="mb-3">
                <label className="block text-sm font-medium text-gray-700">Full Name</label>
                <input
                  value={contactName}
                  placeholder="e.g. John Doe"
                  onChange={(e) => setContactName(e.target.value)}
                  className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                />
              </div>

              <div className="mb-3">
                <label className="block text-sm font-medium text-gray-700">Phone</label>
                <PhoneInput
                  country={'in'}
                  enableSearch
                  value={contactPhone}
                  onChange={(phone) => setContactPhone(phone)}
                  inputProps={{ name: 'phone', required: true, autoFocus: false }}
                  containerClass="react-phone-input-container"
                  inputClass="w-full"
                />
              </div>

              <div className="mb-3">
                <label className="block text-sm font-medium text-gray-700">Email</label>
                <input
                  value={contactEmail}
                  placeholder="you@example.com"
                  onChange={(e) => setContactEmail(e.target.value)}
                  className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                />
              </div>

              <div className="mb-3">
                <label className="block text-sm font-medium text-gray-700">Preferred Travel Date</label>
                <input
                  type="date"
                  value={travelDate}
                  onChange={(e) => setTravelDate(e.target.value)}
                  className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-3 mb-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Adults</label>
                  <input
                    type="number"
                    min={1}
                    value={adults}
                    onChange={(e) => setAdults(parseInt(e.target.value || '1'))}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Children</label>
                  <input
                    type="number"
                    min={0}
                    value={children}
                    onChange={(e) => setChildren(parseInt(e.target.value || '0'))}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
              </div>

              <div className="mb-3">
                <label className="block text-sm font-medium text-gray-700">Message</label>
                <textarea
                  value={message}
                  placeholder="Any special requests or notes (optional)"
                  onChange={(e) => setMessage(e.target.value)}
                  rows={3}
                  className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                />
              </div>

              <button
                onClick={handleSubmitBooking}
                className="w-full bg-emerald-600 text-white py-3 rounded-lg hover:bg-emerald-700 transition-colors duration-200 font-medium whitespace-nowrap"
              >
                Submit Booking
              </button>

              <div className="mt-6 pt-6 border-t border-gray-200 text-sm text-gray-600">
                <div className="flex items-center gap-3 mb-2">
                  <i className="ri-shield-check-line text-green-500"></i>
                  <span>Secure SSL encryption</span>
                </div>
                <div className="flex items-center gap-3 mb-2">
                  <i className="ri-refund-line text-blue-500"></i>
                  <span>Free cancellation up to 24 hours</span>
                </div>
                <div className="flex items-center gap-3">
                  <i className="ri-customer-service-line text-purple-500"></i>
                  <span>24/7 customer support</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
